import React, {useEffect, useState} from "react";
import {type UpdateSeedsParams, useCancelHeat, useGetHeatRegistrations, useLaunchHeat, useUpdateSeeds} from "./heatHooks";
import {type Heat, HeatEliminationType, type HeatRegistration, HeatStatus} from '@iziping/common';
import {useTranslation} from "react-i18next";
import EliminationBracket from "../matches/EliminationBracket";
import Groups from "../matches/Groups";
import {useQueryClient} from "@tanstack/react-query";
import { QUERY_CACHE } from '@/model/endpoints';
import { useNotification } from '@iziping/ui';
import {useNavigate} from "react-router-dom";
import SortableList from "../shared/sort/SortableList";
import SortableRow from "../shared/sort/SortableRow";
import HeatRegistrationItem from "../shared/sort/HeatRegistrationItem";
import styles from "@/styles/form.module.css";

interface HeatInitializationProps {
    heat: Heat;
}

const HeatInitialization: React.FC<HeatInitializationProps> = ({heat}) => {
    const {t} = useTranslation();
    const {data: registrations, isLoading, error} = useGetHeatRegistrations(heat.id ?? 0);
    const {notify} = useNotification();
    const navigate = useNavigate();
    const launchHeatMutation = useLaunchHeat();
    const cancelHeatMutation = useCancelHeat();
    const updateSeeds = useUpdateSeeds();
    const queryClient = useQueryClient();


    if (isLoading) return <div>Loading...</div>;
    if (error) {
        if (error.message === 'not.enough.heat.registrations') {
            return <div>Pas assez d'inscrits</div>;
        }
        return <div>Error fetching data</div>;
    }

    const [orderedPlayers, setOrderedPlayers] = useState<HeatRegistration[]>([]);

    useEffect(() => {
        if (registrations) {
            setOrderedPlayers(registrations);
        }
    }, [registrations]);

    const handleLaunchHeat = async () => {
        try {
            await launchHeatMutation.mutateAsync({heatId: heat.id} );
            queryClient.invalidateQueries({queryKey: [QUERY_CACHE.GET_HEAT]})
            notify(t('heat.launched.success'), 'success');
            navigate('/matches/manage');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    const handleCancelHeat = async () => {
        try {
            await cancelHeatMutation.mutateAsync({heatId: heat.id} );
            queryClient.invalidateQueries({queryKey: [QUERY_CACHE.GET_HEAT]})
            notify(t('heat.canceled.success'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    const handleUpdateSeeds = async () => {
        try {
            const payload: UpdateSeedsParams = {
                heatId: heat.id,
                players: orderedPlayers // orderedPlayers doit être de type HeatRegistration[]
            };

            await updateSeeds.mutateAsync(payload);
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.GROUPS]});
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.BRACKET]});
            await queryClient.invalidateQueries({queryKey: [QUERY_CACHE.HEAT_REGISTRATIONS]});
            notify(t('heat.seeds.update.success'), 'success');
        } catch (error: any) {
            notify(t(error.error), 'error');
        }
    };

    return (
        <div>
            <h1>Gérer la série</h1>
            <div className={styles.actionContainer}>
                <button className={'button'} onClick={handleUpdateSeeds}>Mettre à jour</button>
                { heat.status === HeatStatus.PREPARATION ?
                    (<button className={'button'} onClick={() => handleLaunchHeat()}>Lancer série</button>)
                    : (<button className={'button'} onClick={() => handleCancelHeat()}>Annuler série</button>)
                }
            <div className={'container-column'}>
                <div className={'left size-1'}>
                    <SortableList
                        items={orderedPlayers}
                        setItems={setOrderedPlayers}
                        idKey="id"
                        renderItem={(player, index) => (
                            <SortableRow key={player.id} id={player.id} variant={"dense"}>
                                <HeatRegistrationItem registration={player} seed={index+1}/>
                            </SortableRow>
                        )}
                    />
                </div>
                <div className={'right size-2'}>
                    {heat.eliminationType === HeatEliminationType.POULE && (
                        <Groups heatId={heat.id}/>)
                    }
                    {heat.eliminationType === HeatEliminationType.DIRECTE && (
                        <EliminationBracket heatId={heat.id}/>)
                    }
                </div>
            </div>
        </div>
        </div>
    )
}

export default HeatInitialization;